import React from 'react';
import Gmap from './Gmap';
import ShopList from './ShopList';
import loadGoogleMapsApi from 'load-google-maps-api';
import GeolocationWrap from './lib/GeolocationWrap';
import { orgRound } from './util';
import './App.scss';
import _CONST from './const';

class App extends React.Component {
  constructor(props) {
    super(props);

    this.progressOrigin = _CONST.DEFAULT_ORIGIN
    this.progressKeywords = _CONST.DEFAULT_KEYWORDS
    this.progressShopList = [] // 検索前のshoplist

    this.state = { 
      isFatalError: false, // 致命的なエラーがおきたか
      isFetching: true, // 取得中
      origin: this.progressOrigin, // 画面の中心
      keywords: this.progressKeywords,     // 検索ワード
      shopList: this.progressShopList,      // 店舗リスト
      selectedShop: {
        id: null,
        hasDetail: null,
      },
      visibleDistance: false,
    };
    this.textInput = React.createRef();
    this.onSubmit = this.onSubmit.bind(this);
    this.onClickCurrentPosition = this.onClickCurrentPosition.bind(this);
    this.shopClickAction = this.shopClickAction.bind(this);
    this.markerClickAction = this.markerClickAction.bind(this);
  }

  componentDidMount() {
    this.onLoad()
  }


  /**
   * ----------------------------------------------------
   * アクション
   * ----------------------------------------------------
   */

  /**
   * 店舗クリック
   */
  shopClickAction(shopId, hasDetail) {
    this.setState({
      keywords: '',
      selectedShop: {
        id: shopId,
        hasDetail: hasDetail,
      }
    });
  }


  /**
   * マーカークリック
   */
  markerClickAction(shopId, hasDetail) {
    this.setState({
      keywords: '',
      selectedShop: {
        id: shopId,
        hasDetail: hasDetail,
      }
    });
  }


  /**
   * 検索
   */
  searchAction(visibleDistance) {
    this.setState({
      isFetching: false,
      origin: this.progressOrigin,
      keywords: this.progressKeywords,
      shopList: this.progressShopList || [],
      visibleDistance: visibleDistance,
      selectedShop: {
        id: null,
        hasDetail: null,
      }
    });
  }


  /**
   * フェッチ開始アクション
   */
  fetchStartAction() {
    // 取得開始
    this.setState({
      isFetching: true,
    });
  }


  /**
   * 致命的エラー
   */
  fatalErrorAction() {
    this.setState({
      isFetching: false,
      isFatalError: true
    });
  }


  /**
   * ----------------------------------------------------
   * DOM イベント
   * ----------------------------------------------------
   */

  /**
   * ロード
   */
  async onLoad() {
    // gmap, shoplistを取得
    const result = await Promise.all([
      this.getGmapPromise().catch((error) => this.onPromiseError({error, error_type: _CONST.ERROR_TYPE.GMAP})),
      this.getShopPromise().catch((error) => this.onPromiseError({error, error_type: _CONST.ERROR_TYPE.SHOP}))
    ]);

    // check error
    if(this.hasSomeError([result[0], result[1]])){
      return false
    }


    this.googleMaps = this.onGetGmapPromise(result[0])
    console.log(result[1]);
    this.progressShopList = this.onGetShopPromise(result[1])

    // ワード検索から座標取得
    this.progressKeywords = _CONST.DEFAULT_KEYWORDS

    this.search(false)
  }


  /**
   * 現在地ポジション
   */
  async onClickCurrentPosition(event) {
    event.preventDefault();
    if (this.state.isFetching) {
      return false
    }

    // 取得開始
    this.fetchStartAction()

    const currentPosition = await this.getCurrentPosition()
    this.progressKeywords = currentPosition.address
    const visibleDistance = currentPosition.hasError ? false : true
    this.search(visibleDistance)
  }


  /**
   * 検索サブミット
   */
  async onSubmit(event) {
    event.preventDefault();

    this.progressKeywords = this.textInput.current.value;
    // 検索前のショップを詰めておく
    this.progressShopList = this.state.shopList

    // 取得開始
    this.fetchStartAction()

    this.search(true)
  }


  /**
   * ----------------------------------------------------
   * Gmap API 設定
   * ----------------------------------------------------
   */

  /**
   * gmap本体取得
   */
  async getGmapPromise() {
    return loadGoogleMapsApi({
      key: _CONST.GOOGLE_API_KEY,
      libraries: ['geometry'],
      // timeout: 10000, // default
    })
  }


  /**
   * 取得後処理
   */
  onGetGmapPromise(googleMaps) {
    // gmapが正しく取得できてない
    if (!googleMaps.geometry) {
      return false
    }
    return googleMaps;
  }


  /**
   * ----------------------------------------------------
   * 店舗処理
   * ----------------------------------------------------
   */

  /**
   * 店舗情報を取得
   */
  async getShopPromise() {
    return fetch(_CONST.FETCH_SHOPLIST_URL, _CONST.FETCH_SHOPLIST_OPT)
      .then(res => res.json())
  }


  /**
   * 取得後処理
   */
  onGetShopPromise(shopList) {
    if (!shopList) {
      return false
    }
    // 検索ソート前のデータを初期化
    return shopList.map(shop => this.parseShop(shop))
  }


  /**
   * jsonデータパース
   */
  parseShop(data) {
    const shop = {};
    shop.id = data.id;
    shop.type = data.acf.shop_type;
    shop.isOfficial = (shop.type.value === 'official')
    shop.name = data.acf.shop_name;

    if (data.acf.shop_latlng) {
      const latlng = data.acf.shop_latlng.split(',');
      shop.latLng = new this.googleMaps.LatLng(latlng[0], latlng[1]);
    } else {
      shop.latLng = new this.googleMaps.LatLng(1, 1);
    }
    shop.name = data.acf.shop_name;
    shop.address = data.acf.shop_address;
    shop.tel = data.acf.shop_tel;
    shop.distance = null;
    shop.detail = null;

    if (shop.isOfficial) {
      shop.detail = {};
      shop.detail.image = data.acf.shop_detail_image_1;
      if (shop.detail.image) {
        shop.detail.mapImage = data.acf.shop_detail_image_1.sizes.medium;
      }
      shop.detail.siteUrl = data.acf.shop_url;
      shop.detail.businessHour = data.acf.shop_business_hour;
      shop.detail.parking = data.acf.shop_parking;
      shop.detail.service1List = data.acf.shop_service_1 || [];
      shop.detail.service2List = data.acf.shop_service_2 || [];
      shop.detail.service3List = data.acf.shop_service_3 || [];
    } 
    return shop;
  }


  /**
   * 距離情報を追加
   */
  addDistance2ShopList() {
    const shopList = this.progressShopList.map((shop) => {
      let distance = this.googleMaps.geometry.spherical.computeDistanceBetween(this.progressOrigin, shop.latLng);
      distance = orgRound(distance / 1000, 10);
      shop.distance = distance.toString()
      return shop;
    });
    return shopList
  }


  /**
   * 距離ソート
   */
  sort(list) {
    // 破壊的
    list.sort(function (a, b) {
      if (parseFloat(a.distance) > parseFloat(b.distance))
        return 1;
      if (parseFloat(a.distance) < parseFloat(b.distance))
        return -1;
      // a must be equal to b
      return 0;
    });

    return list;
  }


  /**
   * ----------------------------------------------------
   * 位置情報
   * ----------------------------------------------------
   */

  /**
   * 位置情報から住所テキスト取得
   */
  async getCurrentPosition(event) {
    // 位置情報
    const geo = await this.getGeoPositionPromise().catch((error) => this.onPromiseError({error, error_type: _CONST.ERROR_TYPE.GEO}))
    let currentOrigin = this.onGetGeoPositionPromise(geo)

    // 位置情報から住所テキスト取得
    const address = await this.getAddressPromise(currentOrigin).catch((error) => this.onPromiseError({error, error_type: _CONST.ERROR_TYPE.ADDRESS}))

    // errorチェック
    const hasError = this.hasSomeError([geo, address])

    const result = {
      address: address || _CONST.DEFAULT_KEYWORDS,
      hasError
    }

    return result
  }


  /**
   * 位置情報を取得
   */
  async getGeoPositionPromise() {
    this.geolocationWrap = new GeolocationWrap()
    if (!this.geolocationWrap.hasGeolocationApi()) {
      return false
    }
    return this.geolocationWrap.getPosition()
  }


  /**
   * 取得後処理
   */
  onGetGeoPositionPromise(currentPosition) {
    // positionが取得できてない
    if (!currentPosition.coords) {
      return false
    }
    return new this.googleMaps.LatLng(currentPosition.coords.latitude, currentPosition.coords.longitude)
  }


  /**
   * ----------------------------------------------------
   * 検索処理
   * ----------------------------------------------------
   */

  /**
   * 検索処理
   */

  async search(visibleDistance) {
    // ワード検索から座標取得
    const searchGeocode = await this.searchPromise()
      .catch((error) => this.onPromiseError({error, error_type: _CONST.ERROR_TYPE.SEARCH}))

    // errorチェック
    if(!this.hasSomeError([searchGeocode])){
      // エラーがなければ値を設定
      this.onSearchPromise(searchGeocode)
    }

    this.searchAction(visibleDistance);
  }


  /**
   * ワードから座標情報を取得
   */
  async searchPromise() {
    const geocoder = new this.googleMaps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode({ address: this.progressKeywords, region: 'jp' }, (results, status) => {
        if (status === this.googleMaps.GeocoderStatus.OK) {
          resolve(results);
        }
        else {
          reject(status);
        }
      });
    });
  }


  /**
   * 取得後処理
   */
  onSearchPromise(searchGeocode) {
    if (!searchGeocode) {
      return false
    }

    // 検索結果をセット
    this.progressOrigin = searchGeocode[0].geometry.location;
    this.progressShopList = this.sort(this.addDistance2ShopList())

    return true
  }


  /**
   * 座標から住所を取得
   */
  async getAddressPromise(latLng) {

    if(!latLng){
      return false
    }

    var geocoder = new this.googleMaps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ latLng: latLng }, (results, status) => {
        if (status === this.googleMaps.GeocoderStatus.OK) {
          resolve(results[0].formatted_address);
        } else {
          reject(status);
        }
      });
    });
  }


  /**
   * ----------------------------------------------------
   * エラー
   * ----------------------------------------------------
   */

  /**
   * ロード時のエラーデータを作成
   */
  onPromiseError({ error_type, error }) {
    return {
      error_type,
      error,
    }
  }


  /**
   * エラーが一つでもあるか
   */
  hasSomeError(list) {
    const errorList = list.filter((item) => {
      return item && item.hasOwnProperty('error_type')
    })

    if(errorList.length > 0) {
      this.onError(errorList)
      return true
    }

    return false
  }


  /**
   * エラー共通
   */
  onError(errorList) {
    errorList.forEach(item => {
      console.error(item.error_type, item.error);
      if(item.error_type === _CONST.ERROR_TYPE.GMAP) {
        this.onGmapError(item.error)
      }

      if(item.error_type === _CONST.ERROR_TYPE.SHOP) {
        this.onShopError(item.error)
      }

      if(item.error_type === _CONST.ERROR_TYPE.GEO) {
        this.onGeoError(item.error)
      }

      if(item.error_type === _CONST.ERROR_TYPE.ADDRESS) {
        this.onAddressError(item.error)
      }

      if(item.error_type === _CONST.ERROR_TYPE.SEARCH) {
        this.onSearchError(item.error)
      }
    });
  }


  /**
   * Gmap error
   * 致命的エラー
   */
  onGmapError(error) {
    this.fatalErrorAction()
  }


  /**
   * 店舗データ error
   * 致命的エラー
   */
  onShopError(error) {
    this.fatalErrorAction()
  }


  /**
   * 現在地情報 error
   * 初期値を設定
   */
  onGeoError(error) {
    this.geolocationWrap.handleError(error)
    this.progressOrigin = _CONST.DEFAULT_ORIGIN
    this.progressKeywords = _CONST.DEFAULT_KEYWORDS
  }


  /**
   * 住所変換 error
   * 初期値を設定
   */
  onAddressError(error) {
    if (error === 'OVER_QUERY_LIMIT') {
      // リクエストが多すぎる場合
      this.fatalErrorAction()
    }

    this.progressOrigin = _CONST.DEFAULT_ORIGIN
    this.progressKeywords = _CONST.DEFAULT_KEYWORDS
  }


  /**
   * ワード検索 error
   * 初期値を設定
   */
  onSearchError(error) {
    if (error === 'OVER_QUERY_LIMIT') {
      // リクエストが多すぎる場合
      this.fatalErrorAction()
    }

    this.progressOrigin = _CONST.DEFAULT_ORIGIN
    this.progressKeywords = _CONST.DEFAULT_KEYWORDS
  }


  /**
   * ----------------------------------------------------
   * レンダリング
   * ----------------------------------------------------
   */
  render() {
    const { isFatalError, isFetching, shopList, origin, keywords, selectedShop, visibleDistance } = this.state;
    return !isFatalError ? (
        <div className='map'>
          <div className='map__body'>
            <div className='map__canvas'>
              <div className='map__currentPosition'>
                <span className='currentPositionBtn' onClick={this.onClickCurrentPosition}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.813244 5.88752C0.539962 5.8192 0.503003 5.44595 0.757579 5.32536L11.2409 0.359592C11.4956 0.23892 11.7611 0.504387 11.6404 0.75914L6.67464 11.2424C6.55405 11.497 6.1808 11.46 6.11248 11.1868L5.09629 7.12199C5.06942 7.01451 4.98549 6.93058 4.87801 6.90371L0.813244 5.88752Z" fill="white"/>
                  </svg>
                  現在地から探す
                </span>
              </div>
              {!isFetching && (
                <Gmap
                  googleMaps={this.googleMaps}
                  shopList={shopList}
                  origin={origin}
                  keywords={keywords}
                  selectedShop={selectedShop}
                  markerClickAction={this.markerClickAction}
                />
              )}
              {isFetching && <div className='map__message'><span className='map__flash'>Loading Map...</span></div>}
            </div>


            <div className='map__shop'>
              <form className='map__form' onSubmit={this.onSubmit}>
                <input className='map__input' type='text' ref={this.textInput} placeholder='検索' />
                {/* <input type="submit" value="Submit" /> */}
                <i className='map__icon'>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/></svg>
                </i>
              </form>
                <div className='map__list'>
                  <ShopList isFetching={isFetching} shopList={shopList} visibleDistance={visibleDistance} selectedShop={selectedShop} shopClickAction={this.shopClickAction}/>
                </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="map__error"> 店舗情報の取得に失敗しました。時間を置いてもう一度お試しください。 </div>
      )
  }


}

export default App;
